import { useCallback, useEffect, useRef } from "react"
import { isNumber } from "radash"

import type { V2CustomerDirectionsResponse } from "@be-miniapp/api"
import type { FullEngagementInfo } from "@be-miniapp/types"
import { fitBound } from "@be-miniapp/ui/lib/map"

import { decodePath } from "./map"

export interface RideMapInstanceLike {
  getZoom: () => number
  setZoom: (zoom: number) => void
  flyTo: (options: {
    center: [number, number]
    zoom: number
    duration: number
  }) => void
}

export interface RideMapRefLike {
  getMap?: () => RideMapInstanceLike
}

export interface UseRideMapBoundsOptions {
  currentState: string
  pickup?: { location?: { latitude?: number; longitude?: number } }
  dropoff?: { location?: { latitude?: number; longitude?: number } }
  engagement?: FullEngagementInfo
  routeUserDriver?: V2CustomerDirectionsResponse
  routePerfectRide?: V2CustomerDirectionsResponse
  isMapReady: boolean
  mapRef?: RideMapRefLike | null
  enableAutoFocus?: boolean
}

export const useMapBounds = ({
  currentState,
  pickup,
  dropoff,
  engagement,
  routeUserDriver,
  routePerfectRide,
  isMapReady,
  mapRef,
  enableAutoFocus = false,
}: UseRideMapBoundsOptions) => {
  const autoFocusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )
  const lastFocusTimeRef = useRef<number>(0)

  const tryFitBound = useCallback(
    (targetMapRef?: RideMapRefLike | null) => {
      const activeMapRef = targetMapRef || mapRef
      if (currentState === "finding-driver" || !isMapReady || !activeMapRef)
        return

      try {
        const bound: number[][] = []

        if (
          currentState === "driver-found" ||
          currentState === "driver-arrived" ||
          currentState === "perfect-ride"
        ) {
          if (
            isNumber(pickup?.location?.latitude) &&
            isNumber(pickup?.location?.longitude)
          ) {
            bound.push([pickup.location.latitude, pickup.location.longitude])
          }

          if (
            isNumber(engagement?.current_location_latitude) &&
            isNumber(engagement?.current_location_longitude)
          ) {
            bound.push([
              engagement.current_location_latitude,
              engagement.current_location_longitude,
            ])
          }

          if (
            (currentState === "driver-found" ||
              currentState === "driver-arrived") &&
            bound.length >= 2
          ) {
            fitBound(activeMapRef, bound, {
              top: 120,
              right: 80,
              left: 80,
              bottom: 400,
            })

            setTimeout(() => {
              const map = activeMapRef.getMap?.()
              const currentZoom = map?.getZoom()
              if (map && currentZoom !== undefined && currentZoom < 14) {
                map.setZoom(14)
              }
            }, 600)

            return
          }

          const userDriverPath =
            routeUserDriver?.direction?.routes?.[0]?.overview_polyline?.points
          if (userDriverPath) {
            decodePath(userDriverPath).forEach((point) => {
              if (isNumber(point.lat) && isNumber(point.lng)) {
                bound.push([point.lat, point.lng])
              }
            })
          }

          if (
            currentState === "perfect-ride" &&
            isNumber(engagement?.perfect_latitude) &&
            isNumber(engagement?.perfect_longitude) &&
            engagement.perfect_latitude > 0
          ) {
            bound.push([
              engagement.perfect_latitude,
              engagement.perfect_longitude,
            ])

            const perfectRidePath =
              routePerfectRide?.direction?.routes?.[0]?.overview_polyline
                ?.points
            if (perfectRidePath) {
              decodePath(perfectRidePath).forEach((point) => {
                if (isNumber(point.lat) && isNumber(point.lng)) {
                  bound.push([point.lat, point.lng])
                }
              })
            }
          }

          if (bound.length >= 2) {
            fitBound(activeMapRef, bound, {
              top: 80,
              right: 50,
              left: 50,
              bottom: 350,
            })
          } else if (bound.length === 1) {
            const [lat, lng] = bound[0]
            activeMapRef.getMap?.()?.flyTo({
              center: [lng, lat],
              zoom:
                currentState === "driver-found" ||
                currentState === "driver-arrived"
                  ? 16
                  : 15,
              duration: 500,
            })
          }

          return
        }

        if (currentState === "ride-started") {
          if (
            isNumber(engagement?.current_location_latitude) &&
            isNumber(engagement?.current_location_longitude)
          ) {
            bound.push([
              engagement.current_location_latitude,
              engagement.current_location_longitude,
            ])
          }

          if (
            isNumber(dropoff?.location?.latitude) &&
            isNumber(dropoff?.location?.longitude)
          ) {
            bound.push([dropoff.location.latitude, dropoff.location.longitude])
          }

          if (bound.length >= 2) {
            fitBound(activeMapRef, bound, {
              top: 100,
              right: 60,
              left: 60,
              bottom: 350,
            })
            return
          }

          if (bound.length === 1) {
            const [lat, lng] = bound[0]
            activeMapRef.getMap?.()?.flyTo({
              center: [lng, lat],
              zoom: 15,
              duration: 500,
            })
          }

          return
        }

        if (
          isNumber(engagement?.current_location_latitude) &&
          isNumber(engagement?.current_location_longitude)
        ) {
          bound.push([
            engagement.current_location_latitude,
            engagement.current_location_longitude,
          ])
        }

        if (
          isNumber(dropoff?.location?.latitude) &&
          isNumber(dropoff?.location?.longitude)
        ) {
          bound.push([dropoff.location.latitude, dropoff.location.longitude])
        }

        if (bound.length >= 2) {
          fitBound(
            activeMapRef,
            bound,
            {
              top: 80,
              right: 50,
              left: 50,
              bottom: 350,
            },
            true
          )
        } else if (bound.length === 1) {
          const [lat, lng] = bound[0]
          activeMapRef.getMap?.()?.flyTo({
            center: [lng, lat],
            zoom: 15,
            duration: 500,
          })
        }
      } catch (err) {
        console.error("useMapBounds.tryFitBound", err)
      }
    },
    [
      currentState,
      dropoff?.location?.latitude,
      dropoff?.location?.longitude,
      engagement?.current_location_latitude,
      engagement?.current_location_longitude,
      engagement?.perfect_latitude,
      engagement?.perfect_longitude,
      pickup?.location?.latitude,
      pickup?.location?.longitude,
      routePerfectRide?.direction.routes,
      routeUserDriver?.direction.routes,
      isMapReady,
      mapRef,
    ]
  )

  useEffect(() => {
    if (!enableAutoFocus || !isMapReady || !mapRef) return

    const shouldAutoFocus =
      currentState === "driver-found" ||
      currentState === "driver-arrived" ||
      currentState === "ride-started"

    if (!shouldAutoFocus) {
      if (autoFocusIntervalRef.current) {
        clearInterval(autoFocusIntervalRef.current)
        autoFocusIntervalRef.current = null
      }
      return
    }

    tryFitBound(mapRef)
    lastFocusTimeRef.current = Date.now()

    autoFocusIntervalRef.current = setInterval(() => {
      const timeSinceLastFocus = Date.now() - lastFocusTimeRef.current
      if (timeSinceLastFocus >= 4800) {
        tryFitBound(mapRef)
        lastFocusTimeRef.current = Date.now()
      }
    }, 5000)

    return () => {
      if (autoFocusIntervalRef.current) {
        clearInterval(autoFocusIntervalRef.current)
        autoFocusIntervalRef.current = null
      }
    }
  }, [currentState, enableAutoFocus, isMapReady, mapRef, tryFitBound])

  return {
    tryFitBound,
  }
}
